import React from 'react';

// Class strings for the v9 prototype's .btn, .input, table and .page-header styles,
// shared by the Partners, Integrations and API keys settings pages.
const BTN_BASE =
	'inline-flex items-center justify-center gap-2 border font-medium whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_MD = `${BTN_BASE} px-4 py-2 text-sm leading-5 rounded-lg`;
const BTN_SM = `${BTN_BASE} px-3 py-1 text-xs leading-[18px] rounded-md`;
const PRIMARY =
	'bg-primary-500 hover:bg-primary-600 text-white border-transparent';
const SECONDARY = 'bg-white text-primary-700 border-gray-300 hover:bg-gray-50';
const NEUTRAL = 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50';

export const btn = {
	primary: `${BTN_MD} ${PRIMARY}`,
	neutral: `${BTN_MD} ${NEUTRAL}`,
	primarySm: `${BTN_SM} ${PRIMARY}`,
	secondarySm: `${BTN_SM} ${SECONDARY}`,
	neutralSm: `${BTN_SM} ${NEUTRAL}`,
	link: 'bg-transparent border-0 p-0 text-sm font-medium text-primary-700 hover:underline cursor-pointer',
};

export const card = 'bg-white border border-gray-200 rounded-lg';
export const sectionTitle = 'text-base font-medium text-gray-900';
export const label = 'block text-sm font-medium text-gray-700 mb-2';
export const help = 'text-xs text-gray-500 mt-1';
export const input =
	'block w-full border border-gray-300 rounded-lg bg-white px-4 py-2 text-sm leading-5 text-gray-900 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50';
export const select = `${input} appearance-none cursor-pointer pr-10 bg-no-repeat bg-[length:16px_16px] bg-[position:right_14px_center]`;
export const selectChevron: React.CSSProperties = {
	backgroundImage:
		"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%236b7280' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 6l4 4 4-4'/%3E%3C/svg%3E\")",
};
export const th =
	'bg-gray-50 px-4 py-2.5 text-left text-xs font-medium tracking-[.05em] uppercase text-gray-500 border-b border-gray-200 whitespace-nowrap';
export const td =
	'px-4 py-2.5 border-b border-gray-200 text-sm text-gray-900 align-middle whitespace-nowrap';

interface SettingsPageHeaderProps {
	title: React.ReactNode;
	subtitle: string;
	actions?: React.ReactNode;
}

export const SettingsPageHeader: React.FC<SettingsPageHeaderProps> = ({
	title,
	subtitle,
	actions,
}) => (
	<div className="flex items-center justify-between gap-6">
		<div>
			<div className="text-xl leading-normal font-normal text-gray-900">
				{title}
			</div>
			<div className="mt-1 text-base leading-normal text-gray-500 max-w-[72ch]">
				{subtitle}
			</div>
		</div>
		{actions && (
			<div className="flex items-center gap-3 shrink-0">{actions}</div>
		)}
	</div>
);

interface SwitchProps {
	checked: boolean;
	onChange?: (checked: boolean) => void;
	disabled?: boolean;
	label?: string;
}

export const Switch: React.FC<SwitchProps> = ({
	checked,
	onChange,
	disabled,
	label: ariaLabel,
}) => (
	<button
		type="button"
		role="switch"
		aria-checked={checked}
		aria-label={ariaLabel}
		disabled={disabled}
		onClick={() => onChange?.(!checked)}
		className="inline-flex items-center disabled:cursor-not-allowed"
	>
		<span
			className={`relative w-9 h-5 rounded-full transition-colors ${checked ? 'bg-primary-500' : 'bg-gray-300'}`}
		>
			<span
				className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`}
			/>
		</span>
	</button>
);

export const NeutralPill: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => (
	<span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium leading-[1.3] rounded-full border bg-gray-100 text-gray-600 border-gray-200 whitespace-nowrap align-middle">
		{children}
	</span>
);
